// Widget de calendario de citas del Dashboard (Rediseño mayor 2026-08-31,
// Módulo G) — mismas 4 vistas que el calendario del CRM (mes/semana/día/
// lista, packages/web/src/web/pages/admin/citas.tsx), sobre el MISMO
// endpoint que ya trae las citas del practicante (GET /api/hce/citas) — acá
// solo se agrupa/presenta distinto según la vista, no hay una fuente de
// datos aparte. "mes" agrupa client-side lo que ya vino del rango completo
// del mes; clickear un día lo lleva a la vista "día" de esa fecha.
import type { JSX, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { CitaDashboard, RangoDashboard } from '@/lib/hceVault';
import { clasesDeEtiqueta } from './etiquetaCita';
import { EtiquetaCitaBadge } from './EtiquetaCitaBadge';

type Vista = RangoDashboard | 'lista';

export interface CalendarioCitasProps {
  readonly vista: Vista;
  readonly onVistaChange: (v: Vista) => void;
  readonly fecha: Date;
  readonly onFechaChange: (f: Date) => void;
  readonly citas: readonly CitaDashboard[];
  readonly cargando: boolean;
  readonly onIngresarPaciente: (cita: CitaDashboard) => void;
  readonly onUnirseVideollamada: (cita: CitaDashboard) => void;
  /** Solo AgendaPage.tsx (Fase 4) los pasa — el Dashboard sigue siendo de solo lectura. */
  readonly onReagendar?: (cita: CitaDashboard) => void;
  readonly onCancelar?: (cita: CitaDashboard) => void;
  /**
   * Solo el Dashboard lo pasa: en la vista «Día» se ven directamente `visiblesDia` citas — la anterior, la actual y la siguiente según la hora — y el
   * resto del día queda arriba y abajo, con scroll. Sin esta prop (Agenda) el día se muestra completo.
   */
  readonly visiblesDia?: number;
  /** Solo pruebas: la hora «actual» de la ventana (ms). Sin esto se usa el reloj del equipo y se re-centra cada minuto. */
  readonly ahoraMs?: number;
}

/**
 * Primera fila de la ventana de `n` citas centrada en la «actual»: la que está en curso (empieza antes o ahora y aún no termina) o, si no hay
 * ninguna, la más cercana en el tiempo (en un día pasado quedan las últimas; en uno futuro, las primeras). Con n = 3 la ventana es
 * anterior · actual · siguiente. `null` si no hay más de `n` citas (nada que recortar). Puro. Las citas llegan en orden cronológico.
 */
export function desdeDeVentana(citas: readonly { startsAt: string | null; endsAt: string | null }[], ahoraMs: number, n: number): number | null {
  if (n < 1 || citas.length <= n) return null;
  let centro = 0;
  let mejor = Number.POSITIVE_INFINITY;
  citas.forEach((c, i) => {
    const ini = c.startsAt ? Date.parse(c.startsAt) : Number.NaN;
    if (Number.isNaN(ini)) return;
    const finParsed = c.endsAt ? Date.parse(c.endsAt) : Number.NaN;
    const fin = Number.isNaN(finParsed) ? ini : finParsed;
    const distancia = ahoraMs >= ini && ahoraMs < fin ? 0 : Math.min(Math.abs(ahoraMs - ini), Math.abs(ahoraMs - fin));
    if (distancia < mejor) {
      mejor = distancia; // empate: se queda la más temprana
      centro = i;
    }
  });
  const antes = Math.floor((n - 1) / 2);
  return Math.min(Math.max(centro - antes, 0), citas.length - n);
}

/**
 * Alto que ocupan `n` filas a partir de `desde`, medido sobre las filas reales (varían: los botones envuelven en pantallas angostas). `null` si
 * los datos no sirven. Puro para poder probarlo sin layout.
 */
export function altoDeVentana(filas: readonly { top: number; bottom: number }[], desde: number, n: number): number | null {
  if (n < 1 || desde < 0 || desde + n > filas.length) return null;
  return filas[desde + n - 1]!.bottom - filas[desde]!.top;
}

function horaDe(iso: string | null): string {
  if (!iso) return '--:--';
  return new Date(iso).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function claveDia(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

export function CalendarioCitas(props: CalendarioCitasProps): JSX.Element {
  const { vista, onVistaChange, fecha, onFechaChange, citas, cargando, onIngresarPaciente, onUnirseVideollamada, onReagendar, onCancelar, visiblesDia, ahoraMs } = props;
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-3">
        <div className="flex text-xs font-mono uppercase tracking-wide gap-3">
          {(['dia', 'semana', 'mes', 'lista'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onVistaChange(v)}
              className={vista === v ? 'text-hce-pine border-b-2 border-hce-pine pb-0.5' : 'text-muted-foreground pb-0.5'}
            >
              {t(`dashboard.calendario.vista.${v}`)}
            </button>
          ))}
        </div>
        {vista !== 'lista' && (
          <div className="flex items-center gap-2 text-xs font-mono text-hce-muted">
            <Button variant="outline" size="sm" onClick={() => onFechaChange(desplazar(fecha, vista, -1))}>
              ←
            </Button>
            <span>{fecha.toLocaleDateString('es-EC', { year: 'numeric', month: 'long', day: vista === 'dia' ? 'numeric' : undefined })}</span>
            <Button variant="outline" size="sm" onClick={() => onFechaChange(desplazar(fecha, vista, 1))}>
              →
            </Button>
          </div>
        )}
      </div>

      {cargando ? (
        <p className="p-4 text-sm text-hce-muted">{t('dashboard.cargando')}</p>
      ) : vista === 'mes' ? (
        // Mes y Semana se dibujan SIEMPRE, con o sin citas (bug real
        // 2026-09-10: antes, sin citas en el rango, caían al mismo texto
        // plano "No hay citas" en vez de mostrar su propia estructura de
        // calendario). "Día" queda a propósito con el comportamiento
        // original (texto plano si no hay citas — pedido explícito del
        // usuario, revertido tras probar una línea de tiempo por horas que
        // no se ajustaba a lo que quería). "Lista" tampoco cambia, es un
        // listado plano por diseño.
        <VistaMes citas={citas} fecha={fecha} onDiaClick={(d) => { onFechaChange(d); onVistaChange('dia'); }} />
      ) : vista === 'semana' ? (
        <VistaSemana
          citas={citas}
          fecha={fecha}
          onIngresarPaciente={onIngresarPaciente}
          onUnirseVideollamada={onUnirseVideollamada}
          onReagendar={onReagendar}
          onCancelar={onCancelar}
          onDiaClick={(d) => { onFechaChange(d); onVistaChange('dia'); }}
        />
      ) : citas.length === 0 ? (
        <p className="p-4 text-sm text-hce-muted">{t('dashboard.sinCitas')}</p>
      ) : (
        (() => {
          const lista = (
            <ListaCitas
              citas={citas}
              onIngresarPaciente={onIngresarPaciente}
              onUnirseVideollamada={onUnirseVideollamada}
              onReagendar={onReagendar}
              onCancelar={onCancelar}
            />
          );
          return vista === 'dia' && visiblesDia && citas.length > visiblesDia ? (
            <VentanaCitasCercanas n={visiblesDia} citas={citas} dia={claveDia(fecha.toISOString())} ahoraMs={ahoraMs}>
              {lista}
            </VentanaCitasCercanas>
          ) : (
            lista
          );
        })()
      )}
    </div>
  );
}

/**
 * Vista «Día» del Dashboard: muestra directamente `n` citas — la anterior, la actual y la siguiente según la hora (ver `desdeDeVentana`) — y deja
 * el resto del día arriba y abajo, con scroll. Mide las filas reales (no asume un alto fijo), vuelve a medir si cambia el ancho y se re-centra cada
 * minuto con la hora. Si el médico ya movió el scroll, ni el paso del tiempo ni un refresco en vivo se lo devuelven: respeta lo que está mirando.
 */
function VentanaCitasCercanas({ n, citas, dia, ahoraMs, children }: { n: number; citas: readonly CitaDashboard[]; dia: string; ahoraMs?: number; children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const sinTocar = useRef(true);
  const ultimaPosicion = useRef(-1);
  const [alto, setAlto] = useState<number | null>(null);
  const [reloj, setReloj] = useState(() => Date.now());
  const ahora = ahoraMs ?? reloj;
  const desde = desdeDeVentana(citas, ahora, n) ?? 0;
  const total = citas.length;

  useEffect(() => {
    if (ahoraMs !== undefined) return undefined;
    const id = window.setInterval(() => setReloj(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [ahoraMs]);

  useLayoutEffect(() => {
    sinTocar.current = true; // día nuevo: de nuevo a la ventana de la hora
  }, [dia]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const medir = (): void => {
      const filas = Array.from(el.querySelectorAll<HTMLElement>('[data-fila-cita]')).map((f) => ({ top: f.offsetTop, bottom: f.offsetTop + f.offsetHeight }));
      const h = altoDeVentana(filas, desde, n);
      setAlto(h !== null && h > 0 ? h : null); // sin layout (h = 0) no se recorta
    };
    medir();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, [n, total, dia, desde]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !sinTocar.current) return;
    const fila = el.querySelectorAll<HTMLElement>('[data-fila-cita]')[desde];
    if (!fila) return;
    el.scrollTop = fila.offsetTop;
    ultimaPosicion.current = el.scrollTop;
  }, [alto, total, dia, desde]);

  return (
    <div>
      <p className="border-b border-border bg-secondary/40 px-3 py-1 font-mono text-[11px] text-hce-muted">
        {t('dashboard.calendario.cercanasDelDia', { visibles: n, total })}
      </p>
      <section
        ref={ref}
        aria-label={t('dashboard.calendario.cercanasDelDiaRegion', { visibles: n, total })}
        onScroll={(e) => {
          // El scroll que pone la propia ventana no cuenta; uno del médico sí.
          if (Math.abs(e.currentTarget.scrollTop - ultimaPosicion.current) > 1) sinTocar.current = false;
        }}
        className="relative overflow-y-auto overscroll-contain"
        style={alto !== null ? { maxHeight: alto } : undefined}
      >
        {children}
      </section>
    </div>
  );
}

function desplazar(fecha: Date, vista: RangoDashboard, direccion: 1 | -1): Date {
  const nueva = new Date(fecha);
  if (vista === 'dia') nueva.setDate(nueva.getDate() + direccion);
  else if (vista === 'semana') nueva.setDate(nueva.getDate() + 7 * direccion);
  else nueva.setMonth(nueva.getMonth() + direccion);
  return nueva;
}

/**
 * Fila de UNA cita — extraída (2026-09-10) porque ahora la usan 3 vistas
 * distintas (ListaCitas, VistaSemana, VistaDia): antes de esto solo
 * ListaCitas mostraba el detalle real de una cita, así que semana/día caían
 * a un texto genérico cuando no había citas en el rango, en vez de mostrar
 * su propia estructura de calendario con las citas (si las hay) adentro.
 */
function FilaCita({
  cita,
  onIngresarPaciente,
  onUnirseVideollamada,
  onReagendar,
  onCancelar,
}: {
  cita: CitaDashboard;
  onIngresarPaciente: (c: CitaDashboard) => void;
  onUnirseVideollamada: (c: CitaDashboard) => void;
  onReagendar?: (c: CitaDashboard) => void;
  onCancelar?: (c: CitaDashboard) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div data-fila-cita className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 ${clasesDeEtiqueta(cita.etiqueta).borde}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="font-mono text-xs text-hce-muted shrink-0">{horaDe(cita.startsAt)}</span>
        <span className="truncate text-sm text-hce-ink">{cita.contactName}</span>
        <EtiquetaCitaBadge etiqueta={cita.etiqueta} turno={cita.turno} />
        <Badge variant={cita.modalidad === 'telemedicina' ? 'pine' : 'outline'}>
          {cita.modalidad === 'telemedicina' ? t('dashboard.modalidad.telemedicina') : t('dashboard.modalidad.presencial')}
        </Badge>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {!cita.medplumPatientId ? (
          <span className="text-xs text-hce-muted">{t('dashboard.pacienteSinSincronizar')}</span>
        ) : cita.modalidad === 'telemedicina' ? (
          <Button size="sm" onClick={() => onUnirseVideollamada(cita)}>
            {t('dashboard.unirseVideollamada')}
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onIngresarPaciente(cita)}>
            {t('dashboard.ingresarPaciente')}
          </Button>
        )}
        {onReagendar && (
          <Button size="sm" variant="ghost" onClick={() => onReagendar(cita)}>
            {t('agenda.reagendar')}
          </Button>
        )}
        {onCancelar && (
          <Button size="sm" variant="ghost" onClick={() => onCancelar(cita)}>
            {t('agenda.cancelar')}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Vista "Lista" — la única que sigue siendo un listado plano agrupado por día, sin grilla (pensada para recorrer muchas citas seguidas, no para ubicarse en el tiempo). */
function ListaCitas({
  citas,
  onIngresarPaciente,
  onUnirseVideollamada,
  onReagendar,
  onCancelar,
}: {
  citas: readonly CitaDashboard[];
  onIngresarPaciente: (c: CitaDashboard) => void;
  onUnirseVideollamada: (c: CitaDashboard) => void;
  onReagendar?: (c: CitaDashboard) => void;
  onCancelar?: (c: CitaDashboard) => void;
}): JSX.Element {
  let ultimaClaveDia = '';
  return (
    <div className="divide-y divide-border">
      {citas.map((c) => {
        const nuevoDia = claveDia(c.startsAt) !== ultimaClaveDia;
        if (nuevoDia) ultimaClaveDia = claveDia(c.startsAt);
        return (
          <div key={c.citaId}>
            {nuevoDia && (
              <div className="bg-secondary/40 px-3 py-1 text-[11px] font-mono uppercase tracking-wide text-hce-muted">
                {c.startsAt ? new Date(c.startsAt).toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
              </div>
            )}
            <FilaCita
              cita={c}
              onIngresarPaciente={onIngresarPaciente}
              onUnirseVideollamada={onUnirseVideollamada}
              onReagendar={onReagendar}
              onCancelar={onCancelar}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Grilla real del mes de `fecha` (2026-09-10: antes esto solo dibujaba una
 * tarjeta por cada día que YA tenía alguna cita — un mes sin citas quedaba
 * con la grilla vacía, indistinguible de "no hay nada", y aunque hubiera
 * citas, los días sin ninguna simplemente no existían en la grilla, sin dar
 * noción real de calendario). Ahora arma los 7 días de la semana + TODOS
 * los días del mes (con relleno al inicio para alinear el 1 bajo su día de
 * semana real), y cada casilla muestra su número aunque tenga 0 citas.
 */
function VistaMes({ citas, fecha, onDiaClick }: { citas: readonly CitaDashboard[]; fecha: Date; onDiaClick: (fecha: Date) => void }): JSX.Element {
  const conteoPorDia = new Map<string, number>();
  const etiquetasPorDia = new Map<string, NonNullable<CitaDashboard['etiqueta']>[]>();
  for (const c of citas) {
    if (!c.startsAt) continue;
    const clave = claveDia(c.startsAt);
    conteoPorDia.set(clave, (conteoPorDia.get(clave) ?? 0) + 1);
    if (c.etiqueta) etiquetasPorDia.set(clave, [...(etiquetasPorDia.get(clave) ?? []), c.etiqueta]);
  }

  const anio = fecha.getFullYear();
  const mes = fecha.getMonth();
  const offsetInicio = new Date(anio, mes, 1).getDay(); // 0 = domingo
  const totalDias = new Date(anio, mes + 1, 0).getDate();
  const hoy = new Date();
  const esHoy = (dia: number) => hoy.getFullYear() === anio && hoy.getMonth() === mes && hoy.getDate() === dia;

  // Nombres cortos de día (es-EC) sacados de una semana de referencia
  // conocida (2023-01-01 fue domingo) — evita hardcodear "Dom/Lun/..." a mano.
  const etiquetasSemana = Array.from({ length: 7 }, (_, i) => new Date(2023, 0, 1 + i).toLocaleDateString('es-EC', { weekday: 'short' }));

  return (
    <div className="p-3">
      <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] font-mono uppercase tracking-wide text-hce-muted">
        {etiquetasSemana.map((etiqueta, i) => (
          // eslint-disable-next-line react/no-array-index-key -- 7 etiquetas fijas, nunca reordenan ni cambian de cantidad.
          <div key={i}>{etiqueta}</div>
        ))}
      </div>
      <div className="mt-1.5 grid grid-cols-7 gap-1.5">
        {Array.from({ length: offsetInicio }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- relleno puramente visual, sin datos ni interacción.
          <div key={`relleno-${i}`} />
        ))}
        {Array.from({ length: totalDias }, (_, i) => {
          const dia = i + 1;
          const total = conteoPorDia.get(`${anio}-${mes}-${dia}`) ?? 0;
          return (
            <button
              key={dia}
              type="button"
              onClick={() => onDiaClick(new Date(anio, mes, dia))}
              className={`aspect-square rounded-md border p-1.5 text-left transition-colors hover:border-hce-pine ${
                esHoy(dia) ? 'border-hce-pine bg-hce-pine/5' : 'border-border'
              }`}
            >
              <div className="text-xs font-mono text-hce-muted">{dia}</div>
              {total > 0 && <div className="mt-0.5 text-sm font-medium text-hce-ink">{total}</div>}
              {total > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-0.5">
                  {(etiquetasPorDia.get(`${anio}-${mes}-${dia}`) ?? []).slice(0, 6).map((e, k) => (
                    <span
                      // eslint-disable-next-line react/no-array-index-key -- puntos puramente visuales, sin estado propio.
                      key={k}
                      title={e.texto}
                      data-etiqueta={e.clave}
                      className={`inline-block h-1.5 w-1.5 rounded-full ${clasesDeEtiqueta(e).punto}`}
                    >
                      <span className="sr-only">{e.texto}</span>
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Vista Semana: 7 días fijos (domingo a sábado de la semana de `fecha`),
 * cada uno con sus propias citas adentro (FilaCita) o "sin citas" — antes
 * esta vista era la lista plana de ListaCitas, así que una semana sin
 * ninguna cita caía al mismo texto genérico del resto (bug real 2026-09-10).
 * El encabezado de cada día es clickeable: salta a la Vista Día de esa fecha.
 */
function VistaSemana({
  citas,
  fecha,
  onIngresarPaciente,
  onUnirseVideollamada,
  onReagendar,
  onCancelar,
  onDiaClick,
}: {
  citas: readonly CitaDashboard[];
  fecha: Date;
  onIngresarPaciente: (c: CitaDashboard) => void;
  onUnirseVideollamada: (c: CitaDashboard) => void;
  onReagendar?: (c: CitaDashboard) => void;
  onCancelar?: (c: CitaDashboard) => void;
  onDiaClick: (fecha: Date) => void;
}): JSX.Element {
  const { t } = useTranslation();

  const inicioSemana = new Date(fecha);
  inicioSemana.setHours(0, 0, 0, 0);
  inicioSemana.setDate(inicioSemana.getDate() - inicioSemana.getDay()); // retrocede al domingo

  const porDia = new Map<string, CitaDashboard[]>();
  for (const c of citas) {
    if (!c.startsAt) continue;
    const clave = claveDia(c.startsAt);
    const lista = porDia.get(clave) ?? [];
    lista.push(c);
    porDia.set(clave, lista);
  }

  const hoy = new Date();
  const dias = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(inicioSemana);
    d.setDate(d.getDate() + i);
    return d;
  });

  return (
    <div className="divide-y divide-border">
      {dias.map((d) => {
        const clave = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        const delDia = (porDia.get(clave) ?? []).slice().sort((a, b) => (a.startsAt ?? '').localeCompare(b.startsAt ?? ''));
        const esHoy = d.toDateString() === hoy.toDateString();
        return (
          <div key={clave}>
            <button
              type="button"
              onClick={() => onDiaClick(d)}
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-secondary/50 ${esHoy ? 'bg-hce-pine/5' : 'bg-secondary/40'}`}
            >
              <span className="text-[11px] font-mono uppercase tracking-wide text-hce-muted">
                {d.toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
              {esHoy && <span className="text-[10px] font-mono uppercase text-hce-pine">{t('dashboard.hoy')}</span>}
            </button>
            {delDia.length === 0 ? (
              <p className="px-3 py-2 text-xs text-hce-muted">{t('dashboard.sinCitas')}</p>
            ) : (
              delDia.map((c) => (
                <FilaCita
                  key={c.citaId}
                  cita={c}
                  onIngresarPaciente={onIngresarPaciente}
                  onUnirseVideollamada={onUnirseVideollamada}
                  onReagendar={onReagendar}
                  onCancelar={onCancelar}
                />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}

